import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getInstallPromptMode } from "../../lib/installPrompt";
import {
  dismissInstallPrompt,
  INSTALL_ENGAGEMENT_EVENT,
  markInstallEngagement,
  shouldOfferInstallPrompt,
} from "../../lib/installPromptStorage";
import { isStandaloneDisplay } from "../../lib/pushNotifications";
import {
  INSTALL_PROMPT_OPEN_EVENT,
  useInstallPrompt,
} from "../../hooks/useInstallPrompt";

function InstallInstructions({ mode }) {
  if (mode === "ios") {
    return (
      <ol className="install-prompt-steps">
        <li>Tippe unten auf <strong>Teilen</strong> (Viereck mit Pfeil).</li>
        <li>Wähle <strong>Zum Home-Bildschirm</strong>.</li>
        <li>Tippe auf <strong>Hinzufügen</strong>.</li>
      </ol>
    );
  }

  return (
    <p className="install-prompt-generic-hint">
      {mode === "native"
        ? "Einmal installieren — danach startest du Albis wie eine App."
        : "Im Browser-Menü findest du oft «App installieren» oder «Zum Startbildschirm hinzufügen»."}
    </p>
  );
}

export default function InstallHomeScreenPrompt() {
  const { user } = useAuth();
  const { canNativeInstall, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [forced, setForced] = useState(false);
  const [busy, setBusy] = useState(false);

  const mode = getInstallPromptMode(canNativeInstall);

  const evaluateVisibility = useCallback(() => {
    if (isStandaloneDisplay()) {
      setVisible(false);
      return;
    }
    setVisible(shouldOfferInstallPrompt({ force: forced }));
  }, [forced]);

  useEffect(() => {
    if (user) markInstallEngagement();
    evaluateVisibility();
  }, [user, evaluateVisibility]);

  useEffect(() => {
    const onOpen = () => {
      setForced(true);
      setVisible(shouldOfferInstallPrompt({ force: true }));
    };
    window.addEventListener(INSTALL_PROMPT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(INSTALL_PROMPT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    evaluateVisibility();
  }, [canNativeInstall, evaluateVisibility]);

  useEffect(() => {
    const onEngaged = () => evaluateVisibility();
    window.addEventListener(INSTALL_ENGAGEMENT_EVENT, onEngaged);
    return () => window.removeEventListener(INSTALL_ENGAGEMENT_EVENT, onEngaged);
  }, [evaluateVisibility]);

  if (!visible) return null;

  const handleDismiss = () => {
    if (!forced) dismissInstallPrompt();
    setForced(false);
    setVisible(false);
  };

  const handleInstall = async () => {
    if (mode === "native") {
      setBusy(true);
      try {
        const { outcome } = await promptInstall();
        if (outcome === "accepted") {
          setVisible(false);
          setForced(false);
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    handleDismiss();
  };

  const primaryLabel =
    mode === "native" ? "Albis installieren" : "Verstanden";

  return (
    <div
      className="push-standalone-prompt install-home-screen-prompt"
      role="dialog"
      aria-labelledby="install-prompt-title"
      aria-describedby="install-prompt-desc"
    >
      <div className="push-standalone-prompt-inner">
        <h2 id="install-prompt-title" className="push-standalone-prompt-title">
          Albis immer griffbereit?
        </h2>
        <p id="install-prompt-desc" className="push-standalone-prompt-desc">
          Einmal zum Home-Bildschirm — dann startest du Albis wie eine App und kannst
          Erinnerungen nutzen.
        </p>
        <InstallInstructions mode={mode} />
        <div className="push-standalone-prompt-actions">
          <button
            type="button"
            className="push-standalone-prompt-btn push-standalone-prompt-btn-primary"
            onClick={handleInstall}
            disabled={busy}
          >
            {busy ? "Wird geöffnet…" : primaryLabel}
          </button>
          <button
            type="button"
            className="push-standalone-prompt-btn push-standalone-prompt-btn-secondary"
            onClick={handleDismiss}
            disabled={busy}
          >
            Später
          </button>
        </div>
      </div>
    </div>
  );
}
